import { logEvent } from "@shared/analytics/analytics";
import { IconButton, IconButtonProps } from "@shared/components/IconButton";
import { useToast } from "@shared/hooks/use-toast";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "@shared/ui/use-toast";
import { useValidationContext } from "../../context";
import {
  usePutPassExemptReading,
  useReplaceDuplicateReading,
} from "../../hooks";
import { AddMeterDialog } from "./AddMeterDialog";
import AdjustReadingDialog from "./AdjustReadingDialog";
import CreateServiceOrderDialog from "./CreateServiceOrderDialog";
import { ExemptReadingDialog } from "./ExemptReadingDialog";
import { RevisitReadingDialog } from "./RevisitReadingDialog";

type DialogType =
  | "exempt"
  | "revisit"
  | "adjust"
  | "add-meter"
  | "service-order"
  | null;
type Props = {
  details?: any;
  isManualMeter: boolean;
};

export const ValidationActionButtons: React.FC<Props> = ({
  details,
  isManualMeter,
}) => {
  const [openDialog, setOpenDialog] = useState<DialogType>(null);
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const toastMessage = useToast();

  const status = details?.readingData?.ruleStatus;

  const isDisabledAll = !status || status === "Pass";
  const isDisabledExempt =
    status === "Fail" ||
    status === "RCNT" ||
    status === "Faulty" ||
    isDisabledAll;
  const isDisabledRevisit = status === "Revisit" || isDisabledAll;
  const isDisabledPass = isDisabledAll;
  const isDisabledAdjust = isDisabledAll;

  const { readingId, meterRunId, goToNextReading, duplicateReadingId } =
    useValidationContext();
  const passReading = usePutPassExemptReading(readingId, meterRunId);
  const replaceReading = useReplaceDuplicateReading();
  const handlePassReading = () => {
    const payload = {
      rule_status: 1,
    };
    passReading.mutate(payload, {
      onSuccess: () => {
        logEvent("MX Validation Reading Validated", {
          readingId,
          meterRunId,
        });
        toast({
          variant: "default",
          title: "Success",
          description: "Reading passed successfully",
        });
        goToNextReading();
      },
    });
  };

  const handleReplaceReading = () => {
    const payload = {
      remote_utility_id: getRemoteUtilityId(),
      current_reading_id: Number(readingId),
      duplicate_reading_id: Number(duplicateReadingId),
    };
    replaceReading.mutate(payload, {
      onSuccess: () => {
        toast({
          variant: "default",
          title: "Success",
          description: "Reading Replaced And Passed successfully",
        });
        // goToNextReading();
      },
    });
  };

  const handleIgnoreReading = () => {
    const payload = {
      remote_utility_id: getRemoteUtilityId(),
      current_reading_id: Number(duplicateReadingId),
      duplicate_reading_id: Number(readingId),
    };
    replaceReading.mutate(payload, {
      onSuccess: () => {
        toast({
          variant: "default",
          title: "Success",
          description: "Reading replaced successfully",
        });
        // goToNextReading();
      },
    });
  };

  const actions: IconButtonProps[] = [
    {
      label: "Pass",
      icon: "Check",
      disabled: isDisabledPass,
      className: "bg-green-500 hover:bg-green-600 text-white",
      onClick: () => handlePassReading(),
    },
    {
      label: "Exempt",
      icon: "ShieldCheck",
      disabled: isDisabledExempt,
      className: `${
        isManualMeter || tab === "exemptions"
          ? "hidden"
          : "bg-yellow-500 hover:bg-yellow-600 text-white"
      }`,
      onClick: () => setOpenDialog("exempt"),
    },
    {
      label: "Revisit",
      icon: "RotateCcw",
      disabled: isDisabledRevisit,
      className: `${
        isManualMeter
          ? "hidden"
          : "bg-orange-500 hover:bg-orange-600 text-white"
      }`,
      onClick: () => setOpenDialog("revisit"),
    },
    {
      label: "Adjust",
      icon: "Settings",
      disabled: isDisabledAdjust,
      className: "bg-blue-500 hover:bg-blue-600 text-white",
      onClick: () => setOpenDialog("adjust"),
    },
  ];

  const newReadingsActions: IconButtonProps[] = [
    {
      label: "Add New Meter",
      icon: "Plus",
      variant: "secondary",
      onClick: () => setOpenDialog("add-meter"),
    },
    {
      label: "Create Service Order",
      icon: "FilePlus2",
      variant: "secondary",
      onClick: () => setOpenDialog("service-order"),
    },
  ];

  const duplicateReadingsActions: IconButtonProps[] = [
    {
      label: "Replace duplicate and Pass",
      icon: "Replace",
      className: "bg-purple-500 hover:bg-purple-600 text-white",
      onClick: () => {
        handleReplaceReading();
      },
    },

    {
      label: "Ignore",
      icon: "X",
      className: "bg-gray-500 hover:bg-gray-600 text-white",
      onClick: () => {
        handleIgnoreReading();
      },
    },
  ];

  const buttonsToRender =
    tab === "new-readings"
      ? newReadingsActions
      : tab === "duplicate"
      ? duplicateReadingsActions
      : actions;
  return (
    <>
      <div className="flex flex-wrap gap-3">
        {!["pass", "revisit"].includes(tab) &&
          buttonsToRender.map((action, idx) => (
            <IconButton key={idx} {...action} />
          ))}
      </div>

      <ExemptReadingDialog
        open={openDialog === "exempt"}
        onClose={() => setOpenDialog(null)}
        onExempt={(reasonCode) => {
          console.log("Exempted with reason:", reasonCode);
          setOpenDialog(null);
        }}
      />

      <RevisitReadingDialog
        open={openDialog === "revisit"}
        onClose={() => setOpenDialog(null)}
      />

      <AdjustReadingDialog
        isOpen={openDialog === "adjust"}
        onClose={() => setOpenDialog(null)}
      />

      <AddMeterDialog
        open={openDialog === "add-meter"}
        onClose={() => setOpenDialog(null)}
        meterDetails={{
          deviceNumber: details?.deviceNo,
          assignRoute: details?.routeDetails?.routeName,
          meterTypeDisplay: details?.meterTypeDisplay,
        }}
        meterId={details?.id}
      />

      <CreateServiceOrderDialog
        isOpen={openDialog === "service-order"}
        meterNumber={details?.meterNumber}
        onClose={() => setOpenDialog(null)}
        detail={details}
      />
    </>
  );
};
